
import { useState } from "react";
import { Customer, CustomerContractResult } from "../../../../Model/CustomerModels";
import { DetailsListLayoutMode, DetailsRow, IColumn, IconButton, IDetailsRowProps, Image, ImageFit, IRenderFunction, PrimaryButton, SelectionMode, ShimmeredDetailsList, Stack, Text, Toggle } from "@fluentui/react";
import { ToastService } from "../../../../Core/Toast/ToastService";
import { t } from "i18next";
import { ContractSemaphore, DigitalContractSignLink } from "../../../../Model/CommonModel";
import { HttpHelper } from "../../../../Core/Http/HttpHelper";
import { actionContainerStyles, columnListStyles, contractDetailContainer, contractDetailHeaderContainer, iconStyles, imageStyles, lgColumnListStyles, lgContractDetailHeaderContainer, lgIconStyles, lgImageStyles, lgListStyles, lgSignButtonStyles, lgTitleStyles, listStyles, mdColumnListStyles, mdContractDetailHeaderContainer, mdIconStyles, mdImageStyles, mdListStyles, mdSignButtonStyles, mdTitleStyles, signButtonStyles, smColumnListStyles, smContractDetailHeaderContainer, smIconStyles, smImageStyles, smListStyles, smSignButtonStyles, smTitleStyles, titleStyles, xlColumnListStyles, xlContractDetailHeaderContainer, xlIconStyles, xlImageStyles, xlListStyles, xlSignButtonStyles, xlTitleStyles, xxlColumnListStyles, xxlContractDetailHeaderContainer, xxlIconStyles, xxlImageStyles, xxlListStyles, xxlSignButtonStyles, xxlTitleStyles } from "../Styles/CustomerContractsStyles";
import { BreakpointType } from "../../../Common/Container/ContainerFluid";
import { useBreakpoint } from "../../../Common/Container/ContainerStyles";
import { DashboardButton } from "../../CustomerDashboard/Components/DashboardButtons";
import { lgTitleContainerStyles, mdTitleContainerStyles, smTitleContainerStyles, titleContainerStyles, xlTitleContainerStyles, xxlTitleContainerStyles } from "../../../Common/Register/Components/Common/Styles/CommonStyles";

import signSuccessImg from '../../../../Assets/Images/bkg_contatti.png';
import { CustomerService } from "../../../../ApplicationCode/Customer/CustomerService";

export interface ICustomerContractDetailProps {
    customer: Customer;
    contractData: CustomerContractResult;
    fillCompanyData: () => void;
}

export const CustomerContractDetail: React.FC<ICustomerContractDetailProps> = (props: ICustomerContractDetailProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(!props.contractData.customerDigitalContract.signed);
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    const buildContractDescription = (): string => {
        if (props.contractData.customerDigitalContract && props.contractData.customerDigitalContract.id > 0)
            return props.contractData.customerDigitalContract.digitalContractDescription;

        if (props.contractData.status === ContractSemaphore.White)
            return t('customerCenter:customerContracts:contractSemaphore:white:status')!;
        
        return t('customerCenter:customerContracts:noContractFound')!;
    }
        
    const checkCustomerCompanyData = (): boolean => {
        const current: Customer = props.customer;
        let statusValue = true;
        let customerData = {
            companyName: current.companyName,
            email: current.email,
            firstName: current.firstName,
            lastName: current.lastName,
            phoneNo: current.phoneNo,
            streetAddress: current.streetAddress,
            zipCode: current.zipCode,
            city: current.city,
            county: current.county,
            country: current.country,
            legalRepresentativeFirstName: current.legalRepresentativeFirstName,
            legalRepresentativeLastName: current.legalRepresentativeLastName,
        };

        Object.keys(customerData).forEach((value: string, index: number, array: string[]) => {
            const currentValue = Object.values(customerData)[index];
            
            if (currentValue === undefined || currentValue === null || currentValue === "") {
                statusValue = false;
            }
        });
        
        return statusValue;
    };

    const handleSignLinkClick = (signLink: string) => {
        if(!checkCustomerCompanyData())
            props.fillCompanyData();
        else
            window.open(signLink, '_blank')
    }

    const buildSignListColumns = (): IColumn[] => {
        return [
            {
                key: "order",
                name: "#",
                minWidth: 50,
                maxWidth: 50,
                isResizable: false,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    return item?.stepOrderIndex;
                }
            },
            {
                key: "signLink",
                name: t('customerCenter:customerContracts:signColumns:signLink')!,
                minWidth: 200,
                maxWidth: 200,
                isResizable: true,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    return (
                        <PrimaryButton 
                            onClick={() => handleSignLinkClick(item?.signLink!)}
                            iconProps={{iconName: 'Edit'}}
                            styles={
                                isSM ?
                                    smSignButtonStyles :
                                isMD ?
                                    mdSignButtonStyles :
                                isLG ?
                                    lgSignButtonStyles :
                                isXL ?
                                    xlSignButtonStyles :
                                isXXL ?
                                    xxlSignButtonStyles 
                                :
                                    signButtonStyles
                            }
                        >
                            {t('customerCenter:customerContracts:signColumns:digitalSignature')!}
                        </PrimaryButton>
                    );
                }
            },
            {
                key: "name",
                name: t('customerCenter:customerContracts:signColumns:name')!,
                minWidth: 200,
                maxWidth: 500,
                isResizable: true,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    return item?.stepFirstName + " " + item?.stepLastName;
                }
            },
            {
                key: "email",
                name: t('customerCenter:customerContracts:signColumns:email')!,
                minWidth: 150,
                maxWidth: 3000,
                isResizable: true,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    return item?.stepEmail
                }
            },
            {
                key: "signed",
                name: t('customerCenter:customerContracts:signColumns:signed')!,
                minWidth: 100,
                maxWidth: 150,
                isResizable: true,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    return <Toggle checked={item?.signed}></Toggle>
                }
            },
            {
                key: "signedAt",
                name: t('customerCenter:customerContracts:signColumns:signedAt')!,
                minWidth: 150,
                maxWidth: 5000,
                isResizable: true,
                styles: isSM ? smColumnListStyles : isMD ? mdColumnListStyles : isLG ? lgColumnListStyles : isXL ? xlColumnListStyles : isXXL ? xxlColumnListStyles : columnListStyles,
                onRender: (item: DigitalContractSignLink | undefined, index?, column?) => {
                    if (!item?.signedAt)
                        return <></>

                    let userLocale: string = navigator.language || navigator.languages[0];
                    const intlOptions: Intl.DateTimeFormatOptions = {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: false,
                    };

                    let date = new Date(item.signedAt);

                    if (date.getFullYear() < 2000)
                        return <></>

                    const milliseconds = Date.UTC(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                        date.getHours(),
                        date.getMinutes(),
                        date.getSeconds(),
                    );
                    const localDate = new Date(milliseconds);

                    return Intl.DateTimeFormat(userLocale, intlOptions).format(localDate);
                }
            }
        ];
    };

    const buildRowList: IRenderFunction<IDetailsRowProps> = (props) => {
        if(!props)
            return null;

        return (
            <DetailsRow
                {...props}
                styles={
                    isSM ?
                        smListStyles :
                    isMD ?
                        mdListStyles :
                    isLG ?
                        lgListStyles :
                    isXL ?
                        xlListStyles :
                    isXXL ?
                        xxlListStyles
                    :
                        listStyles
                }
            />
        );
    }
    
    const downloadSigned = () => {
        let instance = HttpHelper.buildAxiosInstance();
        let endpoint = "/api/customerDigitalContract/namirial/envelope/download/id/"+ props.contractData.customerDigitalContract.envelopeId;
        var promise = new Promise((resolve,reject)=>{
            instance.get(endpoint, {responseType: 'blob'})
            .then((resp)=>{
                let extension: string = "";
                if (resp.data.type === "application/zip")
                    extension = ".zip";
                else if (resp.data.type === "application/pdf")
                    extension = ".pdf";

                const url = window.URL.createObjectURL(new Blob([resp.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', props.contractData.customerDigitalContract.digitalContractDescription+extension);
                document.body.appendChild(link);
                link.click();
                resolve(true);
            })
            .catch((resp)=>{
                console.log(resp.response.data.message);
                reject(resp.response.data.message);
            })
        })

        ToastService.showPromiseMessage(
            promise,
            t('customerCenter:customerContracts:download:inProgress')!,
            t('customerCenter:customerContracts:download:success')!,
            t('customerCenter:customerContracts:download:failed')!
        );
    }

    return (
        <Stack
            verticalFill
            verticalAlign="start"
            className={contractDetailContainer}
        >
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                className={
                    isSM ?
                        smContractDetailHeaderContainer :
                    isMD ?
                        mdContractDetailHeaderContainer :
                    isLG ? 
                        lgContractDetailHeaderContainer :
                    isXL ?
                        xlContractDetailHeaderContainer :
                    isXXL ?
                        xxlContractDetailHeaderContainer
                    :
                        contractDetailHeaderContainer
                }
                onClick={() => setIsOpen(!isOpen)}
            >
                <Stack
                    className={
                        isSM ? 
                            smTitleStyles :
                        isMD ? 
                            mdTitleStyles :
                        isLG ?
                            lgTitleStyles :
                        isXL ?
                            xlTitleStyles :
                        isXXL ?
                            xxlTitleStyles
                        :
                            titleStyles
                    }
                >
                    {buildContractDescription()}
                </Stack>
                <Stack>
                    <IconButton
                        styles={
                            isSM ? 
                                smIconStyles :
                            isMD ?
                                mdIconStyles :
                            isLG ?
                                lgIconStyles :
                            isXL ?
                                xlIconStyles :
                            isXXL ?
                                xxlIconStyles 
                            :
                                iconStyles
                        }
                        iconProps={{ iconName: isOpen ? 'ChevronDown' : 'ChevronRight' }}
                        onClick={() => setIsOpen(!isOpen)}
                    />
                </Stack>
            </Stack>
            <>
                {
                    isOpen ?
                        <Stack>
                            {
                                props.contractData.signLinks && props.contractData.signLinks.length > 0 ?
                                    <ShimmeredDetailsList
                                        selectionMode={SelectionMode.none}
                                        items={props.contractData.signLinks}
                                        columns={buildSignListColumns()}
                                        enableShimmer={false}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        onRenderRow={buildRowList}
                                    />
                                :
                                    <Stack
                                        horizontal={!isSM && !isMD}
                                        verticalFill={isSM || isMD}
                                        verticalAlign="center"
                                        horizontalAlign={"center"}
                                    >
                                        <Stack
                                        >
                                            <Image
                                                className={
                                                    isSM ?
                                                        smImageStyles :
                                                    isMD ?
                                                        mdImageStyles :
                                                    isLG ?
                                                        lgImageStyles :
                                                    isXL ?
                                                        xlImageStyles :
                                                    isXXL ?
                                                        xxlImageStyles
                                                    :
                                                        imageStyles
                                                }
                                                imageFit={ImageFit.cover}
                                                src={signSuccessImg} 
                                            />
                                        </Stack>
                                        <Stack
                                            verticalFill
                                            verticalAlign="center"
                                            className={actionContainerStyles}
                                        >
                                            <Stack
                                                className={
                                                    isSM ? 
                                                        smTitleContainerStyles : 
                                                    isMD ? 
                                                        mdTitleContainerStyles :
                                                    isLG ?
                                                        lgTitleContainerStyles :
                                                    isXL ?
                                                        xlTitleContainerStyles :
                                                    isXXL ?
                                                        xxlTitleContainerStyles
                                                    :
                                                        titleContainerStyles
                                                }
                                                horizontal={isSM || isMD}
                                                horizontalAlign={isSM || isMD ? "center" : "start"}
                                            >
                                                <Text 
                                                    className={
                                                        isSM ? 
                                                            smTitleStyles : 
                                                        isMD ? 
                                                            mdTitleStyles : 
                                                        isLG ?
                                                            lgTitleStyles :
                                                        isXL ?
                                                            xlTitleStyles :
                                                        isXXL ?
                                                            xxlTitleStyles
                                                        :
                                                            titleStyles
                                                    }
                                                >
                                                    {CustomerService.translate("customerContracts:downloadTitle")}
                                                </Text>
                                            </Stack>
                                            <Stack>
                                                <DashboardButton
                                                    enabled
                                                    icon={"Download"}
                                                    title={CustomerService.translate("customerContracts:downloadContract")}
                                                    subtitle={CustomerService.translate("customerContracts:downloadDescription")}
                                                    onClick={() => { 
                                                        downloadSigned();
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Stack>
                            }
                        </Stack>
                    :
                        <></>
                }
            </>
        </Stack>
    );
}